import { useDispatch, useSelector } from 'react-redux';
import React, {
  useMemo,
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Routes from 'lib/utils/routes';
import queryString from 'query-string';

// Components
import { AddWorkOrderModal } from 'components/JobTable/AddWorkOrderModal';

// Selectors
import { getMachine } from 'lib/selectors/getMachine';
import { getNeedsForceLogin } from 'lib/selectors/getCanOpenLaborTicket';
import SwipeUpModal, { IconWrapper } from 'components/SwipeUpModal';
import { Loading } from 'components/Loading';
import useSelectOperator from 'lib/hooks/useSelectOperator';
import {
  actionCloseSelectWorkOrderOp,
  actionOpenSelectWorkOrderOp,
  actionPatchSelectWorkOrderOpOptions,
  actionSetWorkOrderOpActionLoading,
} from 'lib/actions';
import {
  getSelectWorkOrderOpOpen,
  getSelectWorkOrderOpOptions,
} from 'lib/selectors/getSelectWorkOrderOp';
import { getMachineSchedule } from 'lib/selectors/getMachineSchedule';
import { Icon, palette } from '@m12s/component-library';
import { DefaultCardBorder } from 'components/MachineHeader/styled';
import { useTranslation } from 'react-i18next';
import { getProductionScheduleEnabled } from 'lib/selectors/getProductionScheduleEnabled';
import { getEnabledBarcodeFields } from 'lib/selectors/getBarcodeFieldConfig';
import { useRequestMachineSchedule } from 'lib/hooks/useRequestMachineSchedule';
import HorizontalScrollContainer from 'components/HorizontalScrollContainer';
import { smartAppend, replaceField, removeField } from 'lib/utils/barcode';
import { throwSuccessToast, throwErrorToast } from 'lib/utils/toast';
import { getActivityTypes } from 'lib/selectors/getActivityTypes';
import { getPendingActivityTypeRef } from 'lib/selectors/getPendingActivityTypeRef';
import { getIsJobLaborTrackingEnabled } from 'lib/selectors/getIsJobLaborTrackingEnabled';
import { ACTIVITY_MODES } from 'lib/constants';
import useStartNewProductionRun from 'components/SwitchActivityTypeReview/useStartNewProductionRun';
import useSelectActivity from 'lib/hooks/useSelectActivity';
import { findMatchingActivities } from 'lib/utils/activityMatching';
import { getSwitchActivityTypesOptions } from 'lib/selectors/getSwitchActivityTypesOptions';
import useBarcodeScanHandler from 'lib/hooks/useBarcodeScanHandler';
import { BARCODE_HANDLER_PRIORITIES } from 'lib/constants/barcodePriorities';
import useRequestJobStartAt from 'lib/hooks/useRequestJobStartAt';
import useRequestPartCountMultiplier from 'lib/hooks/useRequestPartCountMultiplier';

// misc
import { getHasOpenActivitySet } from 'lib/selectors/getHasOpenActivitySet';
import { getIsAPMEnabled } from 'lib/selectors/getIsAPMEnabled';
import {
  postChangeJobForEvent,
  postSplitProgramEvents,
} from 'lib/api/multiJobSelectRest';
import { falTimes } from 'lib/icons';
import { useHistory } from 'react-router-dom';
import {
  useEffectiveSelectJobRouteState,
  useSelectJobFlowContext,
} from 'pages/SelectJob/SelectJobFlowContext';
import { searchOnProductionScheduleOperations } from './helpers';
import useSelectOperationFlow from './useSelectOperationFlow';
import useCreateActivitySetWithWorkOrder from '../useCreateActivitySetWithWorkOrder';
import Header from './Header';
import NextWorkOrderOpCard from './NextWorkOrderOpCard';
import WorkOrderOpGroup from './WorkOrderOpGroup';
import EmptyMessage from './EmptyMessage';

const SearchWorkOrderContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  padding: 1.5rem;
`;

const StandardWorkOrderContainer = styled.div`
  ${DefaultCardBorder}
  background-color: ${palette.Grey200};
  border-radius: 0.5rem 0 0 0;
  height: 100%;
  overflow: hidden;
  width: 100%;

  flex: 1; /* takes remaining space */
`;

const VerticalScrollContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  min-width: max-content;
  height: 100%;
`;

const SearchRootContainer = styled.div`
  display: flex;
  flex-direction: row;
  overflow: auto;
  overflow-y: hidden;
  height: 100%;
  flex-direction: column;
  overflow-y: auto;
  overflow-x: hidden;
`;

const StandardRootContainer = styled.div`
  display: flex;
  flex-direction: row;
  overflow: auto;
  overflow-y: hidden;
  height: 100%;
`;

const HeaderText = styled.h5`
  font-size: 0.75rem;
  line-height: 0.75rem;
  font-weight: 700;
  color: ${palette.Grey700};
  margin: 0;
`;

const SelectWorkOrderOpModal = ({
  handleOnClose = () => {},
  nextStep: baseNextStep,
  dispatch,
  embeddedInDrawer = false,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const selectJobFlowCtx = useSelectJobFlowContext();
  const effectiveRouteState = useEffectiveSelectJobRouteState(
    history.location.state
  );

  const [manualModalOpen, setManualModalOpen] = useState(false);
  const [newRunModalOpen, setNewRunModalOpen] = useState(false);
  const [selectedField, setSelectedField] = useState(null);

  // Track the last search value that triggered auto-selection to prevent loops
  const lastAutoSelectedSearchRef = useRef(null);
  // Track when a barcode was just scanned (to distinguish from manual edits)
  const barcodeScanOccurredRef = useRef(false);
  const isSubmittingRef = useRef(false);

  const machine = useSelector(getMachine);
  const productionScheduleEnabled = useSelector(getProductionScheduleEnabled);
  const needsForceLogin = useSelector(getNeedsForceLogin);
  const { openSelectOperator, selectOperatorOpen } = useSelectOperator();

  const redirectPath = Routes.machineIdSelectJobPath(machine.id);
  const homePath = Routes.machineIdHomePath(machine.id);
  const hasOpenActivitySet = useSelector(getHasOpenActivitySet);

  const storeDispatch = useDispatch();
  const selectWorkOrderOpOpen = useSelector(getSelectWorkOrderOpOpen);
  const { isSearchMode, search, fromBarcodeScan } = useSelector(
    getSelectWorkOrderOpOptions
  );
  const enabledBarcodeFields = useSelector(getEnabledBarcodeFields);

  // Selectors and hooks needed for handleAutoSelection (matching WorkOrderCard logic)
  const activityTypes = useSelector(getActivityTypes);
  const switchActivityTypes = useSelector(getSwitchActivityTypesOptions);
  const pendingActivityTypeRef = useSelector(getPendingActivityTypeRef);
  const isJobLaborTrackingEnabled = useSelector(getIsJobLaborTrackingEnabled);
  const { openSelectActivity, selectActivityOpen } = useSelectActivity();
  const startNewProductionRun = useStartNewProductionRun();
  const requestJobStartAt = useRequestJobStartAt();
  const requestPartCountMultiplier = useRequestPartCountMultiplier();
  const isAPMEnabled = useSelector(getIsAPMEnabled);

  const setupActivityTypes = activityTypes.filter((type) => {
    return type.activityMode === ACTIVITY_MODES.SETUP;
  });
  const shouldStartAsSetup =
    !isJobLaborTrackingEnabled && setupActivityTypes?.length === 1;

  // Track matched activity type from barcode scan (e.g., scanning "SETUP")
  const [matchedActivityTypeRef, setMatchedActivityTypeRef] = useState(null);

  const InnerContainer = isSearchMode
    ? VerticalScrollContainer
    : HorizontalScrollContainer;

  // Helper functions to prevent code duplication
  const ensureModalClosed = useCallback(() => {
    if (selectWorkOrderOpOpen) {
      storeDispatch(actionCloseSelectWorkOrderOp());
    }
  }, [selectWorkOrderOpOpen, storeDispatch]);

  const ensureModalOpen = useCallback(() => {
    if (!selectWorkOrderOpOpen) {
      storeDispatch(actionOpenSelectWorkOrderOp());
    }
  }, [selectWorkOrderOpOpen, storeDispatch]);

  const changeSearchMode = () => {
    storeDispatch(
      actionPatchSelectWorkOrderOpOptions({
        isSearchMode: !isSearchMode,
      })
    );
  };

  // Handle barcode scan events when on SelectJob page
  const handleBarcodeScan = useCallback(
    (barcode) => {
      throwSuccessToast(`Barcode scanned: ${barcode}`);

      // First, check if the barcode matches an activity type (e.g., "SETUP", "Production")
      const activityMatches = findMatchingActivities(
        barcode,
        switchActivityTypes
      );

      if (activityMatches.length === 1) {
        // Activity type matched! Store it for auto-selection flow
        const matchedActivity = activityMatches[0];
        setMatchedActivityTypeRef(matchedActivity.activityTypeRef);
        throwSuccessToast(`Activity matched: ${matchedActivity.name}`);

        // Don't add to search - just mark that we have a matched activity
        // The auto-selection will use this when a single work order is found
        return;
      }

      if (activityMatches.length > 1) {
        // Multiple activity types matched
        throwErrorToast('Error: Multiple activities found');
        return;
      }

      // No activity match - proceed with work order search logic
      // If modal is not in search mode, switch to it
      if (!isSearchMode) {
        storeDispatch(
          actionPatchSelectWorkOrderOpOptions({
            isSearchMode: true,
          })
        );
      }

      const currentSearch = search || '';
      let updatedSearch;

      // If a field is selected, replace its value
      if (selectedField) {
        updatedSearch = replaceField(
          currentSearch,
          selectedField,
          barcode,
          enabledBarcodeFields
        );
        setSelectedField(null); // Clear selection after replacement
      } else {
        // Otherwise, append to existing search using smart append
        updatedSearch = smartAppend(
          currentSearch,
          barcode,
          enabledBarcodeFields
        );
      }

      // Mark that a barcode scan occurred (for auto-selection logic)
      barcodeScanOccurredRef.current = true;

      // Update search in Redux to trigger filtering
      storeDispatch(
        actionPatchSelectWorkOrderOpOptions({
          isSearchMode: true,
          search: updatedSearch,
          fromBarcodeScan: true, // Mark for potential auto-selection
        })
      );
    },
    [
      storeDispatch,
      isSearchMode,
      search,
      enabledBarcodeFields,
      selectedField,
      switchActivityTypes,
    ]
  );

  // Detect if search came from barcode scan on mount (from Machine.js navigation)
  // This ensures auto-selection works when navigating with an initial barcode scan
  useEffect(() => {
    if (fromBarcodeScan && !lastAutoSelectedSearchRef.current) {
      // Search came from a barcode scan (set by Machine.js or handleBarcodeScan)
      barcodeScanOccurredRef.current = true;
      // Clear the flag so it doesn't persist
      storeDispatch(
        actionPatchSelectWorkOrderOpOptions({
          fromBarcodeScan: false,
        })
      );
    }
  }, [fromBarcodeScan, storeDispatch]); // Run when fromBarcodeScan changes

  // Determine if we should allow single-character scans
  // Only allow minLength: 1 when we already have a workOrderId (subsequent fields can be single digit)
  const hasWorkOrderId = search?.includes('workOrderId:');
  const scannerMinLength = hasWorkOrderId ? 1 : 2;

  // Memoize scanner options to prevent unnecessary re-initialization
  const scannerOptions = useMemo(() => {
    return {
      minLength: scannerMinLength,
    };
  }, [scannerMinLength]);

  // Initialize barcode scanner for SelectJob page (only when modal is open)
  useBarcodeScanHandler('select-work-order', handleBarcodeScan, {
    priority: BARCODE_HANDLER_PRIORITIES.SELECT_WORK_ORDER_MODAL,
    enabled: selectWorkOrderOpOpen,
    scanOptions: scannerOptions,
  });

  // Only open modal if no activity is already open (APM allows adding jobs while running)
  useEffect(() => {
    if (embeddedInDrawer) return;
    if (!hasOpenActivitySet || isAPMEnabled) {
      ensureModalOpen();
    } else {
      ensureModalClosed();
      history.push(homePath);
    }
  }, [
    embeddedInDrawer,
    hasOpenActivitySet,
    isAPMEnabled,
    ensureModalOpen,
    ensureModalClosed,
    history,
    homePath,
  ]);

  // Prevent job overlap: redirect to home if activity is already open (APM allows it)
  const nextStep = useCallback(() => {
    if (!hasOpenActivitySet || isAPMEnabled) {
      baseNextStep();
    } else {
      ensureModalClosed();
      history.push(homePath);
    }
  }, [
    hasOpenActivitySet,
    isAPMEnabled,
    baseNextStep,
    ensureModalClosed,
    history,
    homePath,
  ]);

  useEffect(() => {
    if (needsForceLogin && !selectOperatorOpen) {
      openSelectOperator({
        redirectPath,
      });
    }
  }, [needsForceLogin, selectOperatorOpen, openSelectOperator, redirectPath]);

  // Handler for field value changes (manual edit)
  const handleFieldChange = useCallback(
    (fieldName, newValue) => {
      const currentSearch = search || '';
      const updatedSearch = replaceField(
        currentSearch,
        fieldName,
        newValue,
        enabledBarcodeFields
      );

      // Clear barcode scan flag since this is a manual edit
      barcodeScanOccurredRef.current = false;

      storeDispatch(
        actionPatchSelectWorkOrderOpOptions({
          search: updatedSearch,
        })
      );
    },
    [search, enabledBarcodeFields, storeDispatch]
  );

  // Handler for field deletion
  const handleFieldDelete = useCallback(
    (fieldName) => {
      const currentSearch = search || '';
      const updatedSearch = removeField(
        currentSearch,
        fieldName,
        enabledBarcodeFields
      );

      // If deleting the selected field, clear selection
      if (selectedField === fieldName) {
        setSelectedField(null);
      }

      // Clear barcode scan flag since this is a manual deletion
      barcodeScanOccurredRef.current = false;

      storeDispatch(
        actionPatchSelectWorkOrderOpOptions({
          search: updatedSearch,
        })
      );
    },
    [search, enabledBarcodeFields, selectedField, storeDispatch]
  );

  // Initialize useCreateActivitySetWithWorkOrder hook at component level
  // This hook is used when a matched activity type is found during barcode scan
  // Note: workOrderOp is passed at call time to avoid stale closure issues
  const requestCreateActivitySetWithWorkOrder = useCreateActivitySetWithWorkOrder(
    {
      machine,
      workOrderOp: null, // Will be provided at call time
    }
  );

  // Handler for auto-selecting a work order operation when only one result exists
  // This replicates the logic from WorkOrderCard's handleSelectJob → handleStartAndUpdateManual → handleJobSelected
  // PLUS handles matched activity types from barcode scans (e.g., scanning "SETUP")
  const handleAutoSelection = useCallback(
    async (workOrderOp) => {
      if (!workOrderOp) return;

      // Step 1: Set work order operation in state (same as WorkOrderCard)
      dispatch({
        type: 'SET_WORK_ORDER_OPERATION',
        workOrderOp,
      });

      // Extract the part operation from the work order operation
      const partOperation =
        workOrderOp?.matchingPartOperations?.length > 0
          ? workOrderOp.matchingPartOperations[0]
          : {
              // if no matchingPartOperations, values are included directly on woop
              ...workOrderOp,
            };

      if (!partOperation?.partOperationRef) {
        return;
      }

      // Step 2: Check for query params (like activityTypeRef from Switch Activity flow)
      const queryParams = queryString.parse(history.location.search);
      if (queryParams.activityTypeRef) {
        startNewProductionRun(
          queryParams.activityTypeRef,
          workOrderOp.workOrderId,
          workOrderOp.workOrderOperationRef
        );
        return;
      }

      // APM machines
      if (isAPMEnabled) {
        if (effectiveRouteState?.isSplitChangeJob) {
          const splitCtx = selectJobFlowCtx?.splitJobContext;
          if (splitCtx) {
            try {
              await postSplitProgramEvents(
                splitCtx.machineRef,
                splitCtx.splitTime,
                splitCtx.workOrderOperationRef,
                workOrderOp.workOrderOperationRef
              );
            } catch (err) {
              // eslint-disable-next-line no-console
              console.error('split-change-job failed', err);
            }
          }
          if (selectJobFlowCtx?.onApmFlowComplete) {
            selectJobFlowCtx.onApmFlowComplete();
          } else {
            history.push(Routes.machineIdHomePath(machine.id));
          }
          return;
        }

        const eventId = effectiveRouteState?.eventId;

        if (eventId) {
          if (isSubmittingRef.current) return;
          isSubmittingRef.current = true;

          storeDispatch(actionSetWorkOrderOpActionLoading(true));
          try {
            await postChangeJobForEvent(
              machine.machineRef,
              eventId,
              workOrderOp.workOrderOperationRef,
              effectiveRouteState?.sourceEventRefs
            );
            throwSuccessToast(`Changed to "${workOrderOp.workOrderId}"`);
            window.dispatchEvent(new CustomEvent('apm:opdash-refresh'));
            if (selectJobFlowCtx?.onApmFlowComplete) {
              selectJobFlowCtx.onApmFlowComplete();
            } else {
              history.push(Routes.machineIdHomePath(machine.id));
            }
          } catch (err) {
            // eslint-disable-next-line no-console
            console.error('Failed to change job:', err);
            throwErrorToast('Failed to change job');
          } finally {
            isSubmittingRef.current = false;
            storeDispatch(actionSetWorkOrderOpActionLoading(false));
          }
        } else {
          openSelectActivity({
            workOrderOp,
            partOperation,
            isApmStartJob: true,
          });
          if (selectJobFlowCtx?.onApmFlowComplete) {
            selectJobFlowCtx.onApmFlowComplete();
          } else {
            history.push(Routes.machineIdHomePath(machine.id));
          }
        }
        return;
      }

      // Step 3: Set part operation in state
      dispatch({
        type: 'SET_PART_OPERATION',
        partOperation,
      });

      // Step 4: Check if we have a matched activity type from barcode scan
      if (matchedActivityTypeRef) {
        // Activity type was matched (e.g., "SETUP" was scanned)
        // Respect company configuration for job time prompts
        try {
          const startAt = await requestJobStartAt();

          const defaultPartCountMultiplier =
            workOrderOp?.quantityPerPart ||
            workOrderOp?.quantityPerCycle ||
            partOperation?.quantityPerPart ||
            1;
          const partCountMultiplier = await requestPartCountMultiplier(
            defaultPartCountMultiplier,
            matchedActivityTypeRef
          );

          storeDispatch(actionSetWorkOrderOpActionLoading(true));

          // Pass workOrderOp directly to avoid stale closure issues
          await requestCreateActivitySetWithWorkOrder({
            activityTypeRef: matchedActivityTypeRef,
            partOperation,
            startAt,
            partCountMultiplier,
            workOrderOp,
          });

          // Clear the matched activity type
          setMatchedActivityTypeRef(null);
        } catch (error) {
          // Reset loading state and cleanup
          storeDispatch(actionSetWorkOrderOpActionLoading(false));
          setMatchedActivityTypeRef(null);

          if (error?.code !== 'USER_CANCELLED') {
            throwErrorToast('Failed to start activity');
          }
        }

        return;
      }

      // Step 5: Handle the job selection logic (matching WorkOrderCard's handleJobSelected)
      if (isJobLaborTrackingEnabled) {
        // For labor tracking mode, open activity selection modal
        if (!selectActivityOpen && machine) {
          openSelectActivity({
            workOrderOp,
            partOperation,
          });
        }
      } else {
        // For non-labor tracking mode, set loading state if needed and proceed to next step
        // The next step will handle activity type selection or auto-start with setup
        if (shouldStartAsSetup || pendingActivityTypeRef) {
          storeDispatch(actionSetWorkOrderOpActionLoading(true));
        }
        nextStep();
      }
    },
    [
      dispatch,
      nextStep,
      history,
      machine,
      isJobLaborTrackingEnabled,
      shouldStartAsSetup,
      pendingActivityTypeRef,
      selectActivityOpen,
      startNewProductionRun,
      openSelectActivity,
      storeDispatch,
      matchedActivityTypeRef,
      requestJobStartAt,
      requestPartCountMultiplier,
      requestCreateActivitySetWithWorkOrder,
      isAPMEnabled,
      effectiveRouteState,
      selectJobFlowCtx,
    ]
  );

  const skipRequestMachineSchedule = !productionScheduleEnabled || !machine?.id;
  const { loading: machineScheduleLoading } = useRequestMachineSchedule({
    variables: {
      machineId: machine?.id,
    },
    skip: skipRequestMachineSchedule,
  });

  const {
    machineScheduledWorkOrderOps,
    notMachineScheduledWorkOrderOps,
    pageLoading,
    excludeOperations,
    error,
    Components,
  } = useSelectOperationFlow({
    selectedField,
    onFieldSelect: setSelectedField,
    onFieldChange: handleFieldChange,
    onFieldDelete: handleFieldDelete,
  });

  const machineSchedule = useSelector(getMachineSchedule);

  const { SwitchToggle, SearchInput } = Components;

  const nextWorkOrderOp = useMemo(() => {
    if (machineSchedule?.length) {
      return searchOnProductionScheduleOperations(search, [
        machineSchedule[0],
      ])[0];
    }

    return null;
  }, [machineSchedule, search]);

  const upcomingOperations = useMemo(() => {
    if (machineSchedule?.length) {
      return searchOnProductionScheduleOperations(
        search,
        machineSchedule?.slice(1)
      );
    }

    return [];
  }, [machineSchedule, search]);

  // Calculate all filtered results across all sources
  const allFilteredResults = useMemo(() => {
    const results = [];
    if (nextWorkOrderOp) results.push(nextWorkOrderOp);
    if (upcomingOperations) results.push(...upcomingOperations);
    if (machineScheduledWorkOrderOps)
      results.push(...machineScheduledWorkOrderOps);
    if (notMachineScheduledWorkOrderOps)
      results.push(...notMachineScheduledWorkOrderOps);
    return results;
  }, [
    nextWorkOrderOp,
    upcomingOperations,
    machineScheduledWorkOrderOps,
    notMachineScheduledWorkOrderOps,
  ]);

  // Extract single result if only one match exists
  const singleResult = useMemo(() => {
    return allFilteredResults.length === 1 ? allFilteredResults[0] : null;
  }, [allFilteredResults]);

  // Auto-select work order operation when search narrows to exactly one result after a barcode scan
  useEffect(() => {
    // Reset auto-selection tracking when search is cleared
    if (!search) {
      lastAutoSelectedSearchRef.current = null;
      barcodeScanOccurredRef.current = false;
      return;
    }

    // Check if conditions are met for auto-selection
    const shouldAutoSelect =
      singleResult && // Exactly one result
      !pageLoading && // Not loading
      !machineScheduleLoading && // Not loading schedule
      search && // Search value exists
      search !== lastAutoSelectedSearchRef.current && // Haven't auto-selected for this search yet
      selectWorkOrderOpOpen && // Modal is open
      barcodeScanOccurredRef.current; // Only auto-select after barcode scans, not manual edits

    if (shouldAutoSelect) {
      // Mark this search value as processed
      lastAutoSelectedSearchRef.current = search;
      barcodeScanOccurredRef.current = false; // Reset flag after processing

      // Show user feedback
      throwSuccessToast('1 result found - automatically selected');

      // Trigger the selection
      handleAutoSelection(singleResult);
    }
  }, [
    singleResult,
    pageLoading,
    machineScheduleLoading,
    search,
    selectWorkOrderOpOpen,
    handleAutoSelection,
  ]);

  const showEmptyMessage =
    error ||
    pageLoading ||
    (!nextWorkOrderOp &&
      !machineScheduledWorkOrderOps?.length &&
      !notMachineScheduledWorkOrderOps?.length &&
      !upcomingOperations?.length);

  if (machineScheduleLoading || (needsForceLogin && !selectOperatorOpen)) {
    return <Loading />;
  }

  const WorkOrderContainer = isSearchMode
    ? SearchWorkOrderContainer
    : StandardWorkOrderContainer;

  const RootContainer = isSearchMode
    ? SearchRootContainer
    : StandardRootContainer;

  const headerNode = (
    <Header
      SwitchToggle={SwitchToggle}
      SearchInput={SearchInput}
      changeSearchMode={changeSearchMode}
    />
  );

  const onCloseSelectWorkOrder = () => {
    ensureModalClosed();
    handleOnClose();
  };

  const detailsNode = (
    <>
      {manualModalOpen && (
        <AddWorkOrderModal
          forEditWorkOrderId
          handleOnClose={() => {
            return setManualModalOpen(false);
          }}
        />
      )}
      {newRunModalOpen && (
        <AddWorkOrderModal
          isStopStartNewRun
          handleOnClose={() => {
            setNewRunModalOpen(false);
          }}
        />
      )}
      {showEmptyMessage ? (
        <EmptyMessage loading={pageLoading} error={error} />
      ) : (
        <RootContainer>
          {nextWorkOrderOp && (
            <NextWorkOrderOpCard
              workOrderOp={nextWorkOrderOp}
              nextStep={nextStep}
              dispatch={dispatch}
              key={`${nextWorkOrderOp.workOrderId}-${nextWorkOrderOp.partNumber}-${nextWorkOrderOp.sequenceNumber}`}
            />
          )}

          <WorkOrderContainer>
            <InnerContainer>
              <WorkOrderOpGroup
                title={<HeaderText>{t('Scheduled').toUpperCase()}</HeaderText>}
                items={upcomingOperations}
                nextStep={nextStep}
                dispatch={dispatch}
                excludeOperations={excludeOperations}
              />
              <WorkOrderOpGroup
                title={
                  <HeaderText>{t('Unscheduled').toUpperCase()}</HeaderText>
                }
                items={machineScheduledWorkOrderOps}
                nextStep={nextStep}
                dispatch={dispatch}
                excludeOperations={excludeOperations}
              />
              <WorkOrderOpGroup
                title={
                  <HeaderText>
                    {t('Not assigned to this machine').toUpperCase()}
                  </HeaderText>
                }
                items={notMachineScheduledWorkOrderOps}
                nextStep={nextStep}
                dispatch={dispatch}
                excludeOperations={excludeOperations}
              />
            </InnerContainer>
          </WorkOrderContainer>
        </RootContainer>
      )}
    </>
  );

  if (embeddedInDrawer) {
    // Match SwipeUpModal: one header row = toolbar (search / toggle) + close icon — no separate title bar.
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            padding: '1rem',
            height: '4.5rem',
            color: palette.Grey700,
            backgroundColor: palette.White,
            borderBottom: 'none',
            fontWeight: 500,
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>{headerNode}</div>
          <IconWrapper
            type="button"
            aria-label="Close"
            onClick={onCloseSelectWorkOrder}
          >
            <Icon
              size="2rem"
              color={palette.Grey700}
              icon={falTimes}
              cursor="pointer"
            />
          </IconWrapper>
        </div>
        <div
          style={{
            flex: 1,
            overflow: 'auto',
            backgroundColor: isSearchMode ? palette.Grey200 : palette.White,
            padding: 0,
          }}
        >
          {detailsNode}
        </div>
      </div>
    );
  }

  return (
    <SwipeUpModal
      id="select-work-order-op"
      isOpen={selectWorkOrderOpOpen}
      header={headerNode}
      headerStyles={{
        padding: '1rem',
        height: '4.5rem',
        borderBottom: 'none',
      }}
      onClose={onCloseSelectWorkOrder}
      detailsStyles={{
        backgroundColor: isSearchMode ? palette.Grey200 : palette.White,
        padding: 0,
      }}
      details={detailsNode}
    />
  );
};

SelectWorkOrderOpModal.propTypes = {
  nextStep: PropTypes.func,
  dispatch: PropTypes.func,
  handleOnClose: PropTypes.func,
  embeddedInDrawer: PropTypes.bool,
};

export default SelectWorkOrderOpModal;
